import React, { useRef, useMemo, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface Loading3DProps {
  progress?: number; // 0 to 100
  size?: number;
  primaryColor?: string;
  secondaryColor?: string;
  text?: string;
  subtext?: string;
  className?: string;
  onComplete?: () => void;
}

interface RingProps {
  radius: number;
  tubeRadius: number;
  color: string;
  rotationSpeed: number;
  rotationAxis: [number, number, number];
  progress: number;
  offset: number;
  emissiveIntensity: number;
}

/* ------------------------------------------------------------------ */
/*  Individual Spinning Ring                                           */
/* ------------------------------------------------------------------ */
const Ring: React.FC<RingProps> = React.memo(
  ({
    radius,
    tubeRadius,
    color,
    rotationSpeed,
    rotationAxis,
    progress,
    offset,
    emissiveIntensity,
  }) => {
    const meshRef = useRef<THREE.Mesh>(null);
    const materialRef = useRef<THREE.MeshStandardMaterial>(null);

    useFrame((state) => {
      if (!meshRef.current) return;
      const t = state.clock.elapsedTime;

      // Rotate ring
      meshRef.current.rotation.x +=
        rotationAxis[0] * rotationSpeed * 0.01;
      meshRef.current.rotation.y +=
        rotationAxis[1] * rotationSpeed * 0.01;
      meshRef.current.rotation.z +=
        rotationAxis[2] * rotationSpeed * 0.01;

      // Subtle breathing
      const breathe = Math.sin(t * 2 + offset) * 0.02;
      meshRef.current.scale.setScalar(1 + breathe);

      // Pulse emissive based on progress
      if (materialRef.current) {
        const progressPulse = progress > 0 ? Math.min(progress / 100, 1) : 0;
        const mat = materialRef.current;
        mat.emissiveIntensity = emissiveIntensity + progressPulse * 0.3;
      }
    });

    // Partial ring arc based on progress
    const arcLength = useMemo(() => {
      const baseArc = Math.PI * 1.5; // 270 degrees base
      const progressArc = (progress / 100) * Math.PI * 0.5;
      return baseArc + progressArc;
    }, [progress]);

    return (
      <mesh ref={meshRef} castShadow>
        <torusGeometry args={[radius, tubeRadius, 16, 64, arcLength]} />
        <meshStandardMaterial
          ref={materialRef}
          color={color}
          emissive={color}
          emissiveIntensity={emissiveIntensity}
          roughness={0.4}
          metalness={0.7}
          transparent
          opacity={0.9}
          side={THREE.DoubleSide}
        />
      </mesh>
    );
  }
);

Ring.displayName = 'Ring';

/* ------------------------------------------------------------------ */
/*  Center Sphere                                                      */
/* ------------------------------------------------------------------ */
const CenterSphere: React.FC<{
  color: string;
  progress: number;
}> = React.memo(({ color, progress }) => {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    const scale = 0.3 + (progress / 100) * 0.15;
    meshRef.current.scale.setScalar(scale + Math.sin(t * 3) * 0.02);
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.5}
        roughness={0.2}
        metalness={0.9}
        transparent
        opacity={0.85}
      />
    </mesh>
  );
});

CenterSphere.displayName = 'CenterSphere';

/* ------------------------------------------------------------------ */
/*  Particle Dots around rings                                         */
/* ------------------------------------------------------------------ */
const RingParticles: React.FC<{
  primaryColor: string;
  secondaryColor: string;
}> = React.memo(({ primaryColor, secondaryColor }) => {
  const pointsRef = useRef<THREE.Points>(null);
  const particleCount = 60;

  const { positions, colors } = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    const col = new Float32Array(particleCount * 3);
    const c1 = new THREE.Color(primaryColor);
    const c2 = new THREE.Color(secondaryColor);

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const r = 1.5 + Math.random() * 0.5;
      pos[i * 3] = Math.cos(angle) * r;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 0.3;
      pos[i * 3 + 2] = Math.sin(angle) * r;

      const c = Math.random() > 0.5 ? c1 : c2;
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }

    return { positions: pos, colors: col };
  }, [primaryColor, secondaryColor]);

  useFrame((state) => {
    if (!pointsRef.current) return;
    const t = state.clock.elapsedTime;
    pointsRef.current.rotation.y = t * 0.5;
    pointsRef.current.rotation.x = Math.sin(t * 0.3) * 0.2;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={particleCount}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.04}
        vertexColors
        transparent
        opacity={0.8}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
});

RingParticles.displayName = 'RingParticles';

/* ------------------------------------------------------------------ */
/*  Progress Arc Ring                                                  */
/* ------------------------------------------------------------------ */
const ProgressRing: React.FC<{
  progress: number;
  color: string;
  secondaryColor: string;
}> = React.memo(({ progress, color, secondaryColor }) => {
  const meshRef = useRef<THREE.Mesh>(null);

  const geometry = useMemo(() => {
    return new THREE.RingGeometry(2.2, 2.35, 64, 1, 0, (progress / 100) * Math.PI * 2);
  }, [progress]);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    meshRef.current.rotation.z = -t * 0.3;
  });

  return (
    <mesh ref={meshRef} geometry={geometry} rotation={[0, 0, 0]}>
      <meshStandardMaterial
        color={progress >= 100 ? secondaryColor : color}
        emissive={progress >= 100 ? secondaryColor : color}
        emissiveIntensity={0.4}
        transparent
        opacity={0.8}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
});

ProgressRing.displayName = 'ProgressRing';

/* ------------------------------------------------------------------ */
/*  Outer Glow Ring                                                    */
/* ------------------------------------------------------------------ */
const GlowRing: React.FC<{ color: string }> = React.memo(({ color }) => {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    meshRef.current.rotation.x = Math.sin(t * 0.5) * 0.3;
    meshRef.current.rotation.y = Math.cos(t * 0.4) * 0.3;
  });

  return (
    <mesh ref={meshRef} rotation={[Math.PI / 4, 0, 0]}>
      <torusGeometry args={[2.5, 0.01, 8, 64]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.15}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
});

GlowRing.displayName = 'GlowRing';

/* ------------------------------------------------------------------ */
/*  Loading Text                                                       */
/* ------------------------------------------------------------------ */
const LoadingText: React.FC<{
  progress: number;
  text: string;
  subtext: string;
}> = React.memo(({ progress, text, subtext }) => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (!groupRef.current) return;
    groupRef.current.position.y = -3.5 + Math.sin(state.clock.elapsedTime * 1.5) * 0.05;
  });

  const progressText = `${Math.round(progress)}%`;

  return (
    <group ref={groupRef} position={[0, -3.5, 0]}>
      {/* Main text */}
      <Text
        position={[0, 0.5, 0]}
        fontSize={0.4}
        color="#e2e8f0"
        anchorX="center"
        anchorY="middle"
        font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf"
      >
        {text}
      </Text>

      {/* Progress percentage */}
      <Text
        position={[0, -0.1, 0]}
        fontSize={0.6}
        color="#4299e1"
        anchorX="center"
        anchorY="middle"
        font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf"
      >
        {progressText}
      </Text>

      {/* Subtext */}
      <Text
        position={[0, -0.8, 0]}
        fontSize={0.2}
        color="#718096"
        anchorX="center"
        anchorY="middle"
        font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf"
      >
        {subtext}
      </Text>
    </group>
  );
});

LoadingText.displayName = 'LoadingText';

/* ------------------------------------------------------------------ */
/*  Main Loading Scene                                                 */
/* ------------------------------------------------------------------ */
const LoadingScene: React.FC<{
  progress: number;
  primaryColor: string;
  secondaryColor: string;
  text: string;
  subtext: string;
}> = React.memo(({ progress, primaryColor, secondaryColor, text, subtext }) => {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[3, 5, 3]} intensity={0.8} color="#ffffff" />
      <pointLight position={[-3, -2, 3]} intensity={0.5} color={primaryColor} />

      {/* Ring 1 - Inner, fast */}
      <Ring
        radius={0.8}
        tubeRadius={0.04}
        color={primaryColor}
        rotationSpeed={2}
        rotationAxis={[1, 1, 0]}
        progress={progress}
        offset={0}
        emissiveIntensity={0.4}
      />

      {/* Ring 2 - Middle, medium */}
      <Ring
        radius={1.3}
        tubeRadius={0.035}
        color={secondaryColor}
        rotationSpeed={-1.5}
        rotationAxis={[0, 1, 1]}
        progress={progress}
        offset={Math.PI / 3}
        emissiveIntensity={0.35}
      />

      {/* Ring 3 - Outer, slow */}
      <Ring
        radius={1.8}
        tubeRadius={0.025}
        color={primaryColor}
        rotationSpeed={1}
        rotationAxis={[1, 0, 1]}
        progress={progress}
        offset={(Math.PI * 2) / 3}
        emissiveIntensity={0.3}
      />

      {/* Center sphere */}
      <CenterSphere color={secondaryColor} progress={progress} />

      {/* Particles */}
      <RingParticles primaryColor={primaryColor} secondaryColor={secondaryColor} />

      {/* Progress arc */}
      <ProgressRing
        progress={progress}
        color={primaryColor}
        secondaryColor={secondaryColor}
      />

      {/* Glow ring */}
      <GlowRing color={primaryColor} />

      {/* Text */}
      <LoadingText progress={progress} text={text} subtext={subtext} />

      <fog attach="fog" args={['#0d1117', 8, 18]} />
    </>
  );
});

LoadingScene.displayName = 'LoadingScene';

/* ------------------------------------------------------------------ */
/*  Main Loading3D Component                                           */
/* ------------------------------------------------------------------ */
const Loading3D: React.FC<Loading3DProps> = ({
  progress = 0,
  size = 300,
  primaryColor = '#4299e1',
  secondaryColor = '#d69e2e',
  text = 'Loading...',
  subtext = 'Preparing your experience',
  className = '',
  onComplete,
}) => {
  // Call onComplete when progress reaches 100
  React.useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  return (
    <div
      className={`w-full h-full flex items-center justify-center ${className}`}
      style={{ minHeight: `${size}px`, background: '#0d1117' }}
    >
      <Canvas
        camera={{ position: [0, 0, 6], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 2]}
        style={{ width: '100%', height: '100%', maxHeight: `${size}px` }}
      >
        <LoadingScene
          progress={progress}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          text={text}
          subtext={subtext}
        />
      </Canvas>
    </div>
  );
};

export default React.memo(Loading3D);
